import { devLog, devError } from '../utils/devLogger';
/**
 * Template Review Panel for Facilitators/Admins
 * Review and grade learner submissions of fillable templates
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  Tabs,
  Tab,
  Spinner,
  Row,
  Col,
  InputGroup,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import templateSubmissionService from '../services/templateSubmissionService';
import TemplateSubmissionTracker from './TemplateSubmissionTracker';

const TemplateReviewPanel = () => {
  const { currentUser, userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewComments, setReviewComments] = useState('');
  const [grade, setGrade] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [eligibleLearners, setEligibleLearners] = useState([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitLearnerId, setSubmitLearnerId] = useState('');
  const [submitFile, setSubmitFile] = useState(null);
  const [submitComments, setSubmitComments] = useState('');
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      loadSubmissions(selectedTemplate.id);
      loadEligibleLearners(selectedTemplate);
    }
  }, [selectedTemplate, filterStatus]);

  const templateCohortIds = useMemo(() => {
    if (!selectedTemplate) return [];
    if (Array.isArray(selectedTemplate.cohortIds) && selectedTemplate.cohortIds.length) {
      return selectedTemplate.cohortIds;
    }
    if (selectedTemplate.cohortId) return [selectedTemplate.cohortId];
    return [];
  }, [selectedTemplate]);

  const loadEligibleLearners = async (template) => {
    if (!template) return;
    if (templateCohortIds.length === 0) {
      setEligibleLearners([]);
      return;
    }
    try {
      setLearnersLoading(true);
      const learnerRecords = [];
      for (let i = 0; i < templateCohortIds.length; i += 10) {
        const chunk = templateCohortIds.slice(i, i + 10);
        const learnersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'learner'),
          where('cohortId', 'in', chunk)
        );
        const snapshot = await getDocs(learnersQuery);
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          learnerRecords.push({
            id: docSnap.id,
            name: data.displayName || data.name || data.email || 'N/A',
            email: data.email || '',
            cohortId: data.cohortId || ''
          });
        });
      }
      setEligibleLearners(learnerRecords);
    } catch (err) {
      devError('Error loading eligible learners:', err);
      setEligibleLearners([]);
    } finally {
      setLearnersLoading(false);
    }
  };

  const missingLearners = useMemo(() => {
    if (!eligibleLearners.length) return [];
    const submittedIds = new Set(
      submissions.filter((item) => item.learnerId).map((item) => item.learnerId)
    );
    return eligibleLearners.filter((learner) => !submittedIds.has(learner.id));
  }, [eligibleLearners, submissions]);

  const handleSendMissingReminders = async () => {
    if (!selectedTemplate || missingLearners.length === 0) return;
    try {
      setReminderSending(true);
      const functions = getFunctions();
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
      const subject = `Reminder: ${selectedTemplate.title} submission`;
      const dueDate = selectedTemplate.templateDueDate
        ? new Date(selectedTemplate.templateDueDate).toLocaleDateString()
        : 'N/A';
      const htmlContent = `
        <p>Hello {name},</p>
        <p>This is a reminder to submit: <strong>${selectedTemplate.title}</strong>.</p>
        <p>Due date: <strong>${dueDate}</strong>.</p>
        <p>Please log in to the LMS to upload your submission.</p>
      `;
      const recipients = missingLearners.map((learner) => learner.email).filter(Boolean);
      await sendCustomEmail({ subject, htmlContent, textContent: '', recipients });
      setSuccess('Reminder emails sent to missing learners.');
    } catch (err) {
      devError('Error sending reminder emails:', err);
      setError('Failed to send reminder emails.');
    } finally {
      setReminderSending(false);
    }
  };

  const handleSubmitOnBehalf = async (e) => {
    e.preventDefault();
    if (!selectedTemplate || !submitLearnerId || !submitFile) {
      setError('Select a learner and upload a file.');
      return;
    }
    const learner = eligibleLearners.find((item) => item.id === submitLearnerId);
    if (!learner) {
      setError('Invalid learner selected.');
      return;
    }
    try {
      setSubmitLoading(true);
      setError('');
      setSuccess('');

      const submissionData = {
        templateId: selectedTemplate.id,
        templateTitle: selectedTemplate.title,
        learnerId: learner.id,
        learnerName: learner.name,
        learnerEmail: learner.email,
        comments: submitComments,
        dueDate: selectedTemplate.templateDueDate || null,
        submittedById: currentUser.uid,
        submittedByName: userProfile?.displayName || currentUser.email,
        submittedByRole: userProfile?.role || 'facilitator',
        submittedOnBehalf: true
      };

      await templateSubmissionService.submitTemplate(
        submissionData,
        submitFile,
        (progress) => setSubmitProgress(Math.round(progress))
      );

      setShowSubmitModal(false);
      setSubmitLearnerId('');
      setSubmitFile(null);
      setSubmitComments('');
      setSubmitProgress(0);
      setSuccess('Submission uploaded on behalf of learner.');
      await loadSubmissions(selectedTemplate.id);
    } catch (err) {
      devError('Error submitting on behalf:', err);
      setError('Failed to submit on behalf. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const assignedPrograms = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];
      const assignedCohorts = Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [];
      const filters = {};
      if (userProfile?.role !== 'admin' && userProfile?.role !== 'qa') {
        if (assignedPrograms.length === 1) {
          filters.programId = assignedPrograms[0];
        }
        if (assignedCohorts.length > 0) {
          filters.cohortIds = assignedCohorts;
        }
      }
      const templatesData = await templateSubmissionService.getFillableTemplates(filters);
      setTemplates(templatesData);

      if (templatesData.length > 0) {
        setSelectedTemplate(templatesData[0]);
      }
    } catch (error) {
      devError('Error loading templates:', error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async (templateId) => {
    try {
      setLoading(true);
      const filter = filterStatus !== 'all' ? filterStatus : null;
      const submissionsData = await templateSubmissionService.getTemplateSubmissions(
        templateId,
        filter
      );
      setSubmissions(submissionsData);

      const statsData = await templateSubmissionService.getSubmissionStats(templateId);
      setStats(statsData);
    } catch (error) {
      devError('Error loading submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError('');

      const reviewData = {
        status: reviewStatus,
        reviewerId: currentUser.uid,
        reviewerName: userProfile?.displayName || currentUser.email,
        comments: reviewComments,
        grade: grade || null
      };

      await templateSubmissionService.reviewSubmission(selectedSubmission.id, reviewData);

      setSuccess('Submission reviewed successfully!');
      setShowReviewModal(false);
      resetReviewForm();

      // Reload submissions
      await loadSubmissions(selectedTemplate.id);
    } catch (error) {
      devError('Error reviewing submission:', error);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestResubmit = async (submission) => {
    if (!submission) return;
    const comment = window.prompt('Add a resubmission note (optional):', 'Please resubmit your file.');
    if (comment === null) return;
    try {
      setSubmitting(true);
      setError('');
      const reviewData = {
        status: 'revision_requested',
        reviewerId: currentUser.uid,
        reviewerName: userProfile?.displayName || currentUser.email,
        comments: comment || '',
        grade: submission.grade || null
      };
      await templateSubmissionService.reviewSubmission(submission.id, reviewData);
      setSuccess('Resubmission request sent.');
      await loadSubmissions(selectedTemplate.id);
    } catch (error) {
      devError('Error requesting resubmission:', error);
      setError('Failed to request resubmission.');
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewModal = (submission) => {
    setSelectedSubmission(submission);
    setReviewStatus(submission.status || 'approved');
    setReviewComments(submission.reviewComments || '');
    setGrade(submission.grade || '');
    setShowReviewModal(true);
    setError('');
  };

  const resetReviewForm = () => {
    setSelectedSubmission(null);
    setReviewStatus('approved');
    setReviewComments('');
    setGrade('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'Pending Review' },
      approved: { variant: 'success', text: 'Approved' },
      rejected: { variant: 'danger', text: 'Rejected' },
      revision_requested: { variant: 'info', text: 'Revision Requested' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredSubmissions = submissions.filter((submission) => {
    if (!normalizedSearch) return true;
    const name = (submission.learnerName || '').toLowerCase();
    const email = (submission.learnerEmail || '').toLowerCase();
    return name.includes(normalizedSearch) || email.includes(normalizedSearch);
  });

  if (loading && templates.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading template submissions...</p>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <Alert variant="info">
        <strong>No Fillable Templates</strong>
        <p className="mb-0 mt-2">
          There are currently no fillable templates. Upload a template from the Resource Hub
          and mark it as "fillable" to get started.
        </p>
      </Alert>
    );
  }

  return (
    <div>
      <ToastContainer position="top-end" className="p-3">
        <Toast show={Boolean(success)} onClose={() => setSuccess('')} delay={3000} autohide bg="success">
          <Toast.Body className="text-white">
            {success || 'Review submitted.'}
          </Toast.Body>
        </Toast>
        <Toast show={Boolean(error)} onClose={() => setError('')} delay={4000} autohide bg="danger">
          <Toast.Body className="text-white">
            {error}
          </Toast.Body>
        </Toast>
      </ToastContainer>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Template</Form.Label>
            <Form.Select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                setSelectedTemplate(template);
              }}
            >
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Filter by Status</Form.Label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Submissions</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revision_requested">Revision Requested</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {stats && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <Row className="text-center">
                  <Col>
                    <h3>{stats.total}</h3>
                    <small className="text-muted">Total</small>
                  </Col>
                  <Col>
                    <h3 className="text-warning">{stats.pending}</h3>
                    <small className="text-muted">Pending</small>
                  </Col>
                  <Col>
                    <h3 className="text-success">{stats.approved}</h3>
                    <small className="text-muted">Approved</small>
                  </Col>
                  <Col>
                    <h3 className="text-danger">{stats.rejected}</h3>
                    <small className="text-muted">Rejected</small>
                  </Col>
                  <Col>
                    <h3 className="text-info">{stats.revisionRequested}</h3>
                    <small className="text-muted">Needs Revision</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {selectedTemplate && (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="mb-0">{selectedTemplate.title} - Submissions</h5>
                {selectedTemplate.templateInstructions && (
                  <small className="text-muted">{selectedTemplate.templateInstructions}</small>
                )}
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowSubmitModal(true)}
              >
                Submit on Behalf
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Tabs defaultActiveKey="submissions" className="mb-3">
              <Tab eventKey="submissions" title="Submissions">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search learner name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                </div>
                {loading ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : filteredSubmissions.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    No submissions yet for this template.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Submitted</th>
                          <th>Status</th>
                          <th>Grade</th>
                          <th>Submitted By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubmissions.map(submission => (
                          <tr key={submission.id}>
                            <td>
                              <div>
                                <strong>{submission.learnerName}</strong>
                              </div>
                              <small className="text-muted">{submission.learnerEmail}</small>
                            </td>
                            <td>
                              <div>{submission.submittedAt.toLocaleDateString()}</div>
                              <small className="text-muted">
                                {submission.submittedAt.toLocaleTimeString()}
                              </small>
                            </td>
                            <td>{getStatusBadge(submission.status)}</td>
                            <td>
                              {submission.grade ? (
                                <Badge bg="primary">{submission.grade}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {submission.submittedOnBehalf
                                ? submission.submittedByName || 'Facilitator'
                                : 'Learner'}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => window.open(submission.fileURL, '_blank')}
                              >
                                👁️ View
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-2"
                                onClick={() => window.open(submission.fileURL, '_blank')}
                              >
                                ⬇️ Download
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="me-2"
                                onClick={() => handleRequestResubmit(submission)}
                                disabled={submitting}
                              >
                                🔄 Request Resubmit
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => openReviewModal(submission)}
                              >
                                {submission.status === 'pending' ? '✅ Review' : '✏️ Update Review'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Tab>
              <Tab eventKey="tracking" title="Tracking">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <strong>Missing submissions:</strong> {missingLearners.length}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={missingLearners.length === 0 || reminderSending}
                    onClick={handleSendMissingReminders}
                  >
                    {reminderSending ? 'Sending...' : 'Send Reminders'}
                  </Button>
                </div>
                {missingLearners.length === 0 ? (
                  <Alert variant="light">All learners have submitted.</Alert>
                ) : (
                  <div className="table-responsive mb-3">
                    <Table size="sm" bordered>
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingLearners.map((learner) => (
                          <tr key={learner.id}>
                            <td>{learner.name}</td>
                            <td>{learner.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                <TemplateSubmissionTracker template={selectedTemplate} />
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Review Submission</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleReview}>
          <Modal.Body>
            {selectedSubmission && (
              <>
                <Alert variant="light">
                  <strong>Learner:</strong> {selectedSubmission.learnerName}<br />
                  <strong>Submitted:</strong> {selectedSubmission.submittedAt.toLocaleString()}<br />
                  <strong>Template:</strong> {selectedTemplate?.title}<br />
                  <strong>Submitted By:</strong> {selectedSubmission.submittedOnBehalf
                    ? selectedSubmission.submittedByName || 'Facilitator'
                    : 'Learner'}
                </Alert>

                {selectedSubmission.comments && (
                  <Alert variant="info">
                    <strong>Learner Comments:</strong><br />
                    {selectedSubmission.comments}
                  </Alert>
                )}

                <Button
                  variant="outline-primary"
                  className="mb-3 w-100"
                  onClick={() => window.open(selectedSubmission.fileURL, '_blank')}
                >
                  👁️ View Submitted File
                </Button>

                <Form.Group className="mb-3">
                  <Form.Label>Review Status *</Form.Label>
                  <Form.Select
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    required
                  >
                    <option value="approved">✅ Approved</option>
                    <option value="revision_requested">🔄 Request Revision</option>
                    <option value="rejected">❌ Rejected</option>
                    <option value="pending">⏳ Keep as Pending</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Grade/Score (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    placeholder="e.g., 85%, A+, Pass, 4/5"
                  />
                  <Form.Text className="text-muted">
                    Enter a grade, score, or rating for this submission
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Review Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    placeholder="Provide feedback to the learner..."
                  />
                  <Form.Text className="text-muted">
                    {reviewStatus === 'revision_requested'
                      ? 'Explain what needs to be corrected or improved'
                      : 'Optional: Provide feedback or comments'}
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowReviewModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Submit on Behalf Modal */}
      <Modal show={showSubmitModal} onHide={() => setShowSubmitModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Submit on Behalf</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitOnBehalf}>
          <Modal.Body>
            {templateCohortIds.length === 0 && (
              <Alert variant="warning">
                This template is not assigned to a cohort. Assign cohorts to enable on-behalf submission.
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Select Learner</Form.Label>
              <Form.Select
                value={submitLearnerId}
                onChange={(e) => setSubmitLearnerId(e.target.value)}
                disabled={learnersLoading || templateCohortIds.length === 0}
                required
              >
                <option value="">Select learner...</option>
                {eligibleLearners.map((learner) => (
                  <option key={learner.id} value={learner.id}>
                    {learner.name} ({learner.email})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Upload Completed File</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Comments (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={submitComments}
                onChange={(e) => setSubmitComments(e.target.value)}
                placeholder="Notes about this submission..."
              />
            </Form.Group>
            {submitLoading && (
              <div className="mb-2">
                <div className="text-muted">Uploading... {submitProgress}%</div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubmitModal(false)} disabled={submitLoading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitLoading || templateCohortIds.length === 0}>
              {submitLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TemplateReviewPanel;
